import { useEffect, useState } from "react";
import { useConfirm } from "material-ui-confirm";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { userDisplayName } from "../../pages/events/components/EventFunctions";
import moment from "moment";

import {
  cleanLocation,
  deleteData,
  getData,
} from "../utilities/UtilityFunctions";
import StyledMenu from "./StyledMenu";
import { postNotification } from "../utilities/Notifications";
import ModalLoadingFull from "../modals/ModalLoadingFull";
import {
  ApprovalModal,
  approveOrganization,
} from "../../pages/organizations/components/ApprovalUtilities";
import {
  BCPCircleInfo,
  BCPEllipsisVertical,
  BCPLocationDot,
  BCPUser,
} from "../icons/bcp/BCPIcons";
import { isAppMode } from "../../global/AppOverlay";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function OrganizationCard(props) {
  const {
    organization,
    organize,
    isAdmin,
    deleteOrganization,
    updateOrganization,
    distributorView,
    preventImageFetch,
    activeTab,
  } = props;
  const [photo, setPhoto] = useState(null);
  const theme = useTheme();
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const appMode = isAppMode();

  useEffect(() => {
    const check = async () => {
      setPhoto(null);
      if (organization?.photoFileId) {
        try {
          var apiPhoto = await getData(`/files/${organization.photoFileId}`, {
            queryStringParameters: {
              //resize type?
            },
          });
          setPhoto(apiPhoto);
        } catch (err) {
          console.error(err);
        }
      }
    };

    if (!preventImageFetch) check();

    // eslint-disable-next-line
  }, [props]);

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <CardActionArea
          href={
            isAdmin === true || organize
              ? `/organize/organization/${organization.id}${
                  activeTab ? `?active_tab=${activeTab}` : ""
                }`
              : `/organization/${organization.id}${
                  activeTab ? `?active_tab=${activeTab}` : ""
                }`
          }
          target={appMode ? "_blank" : "_self"}
        >
          <CardMedia
            component="img"
            image={
              photo?.url ||
              organization?.imageURL ||
              organization?.photoUrl ||
              `${getEnvironmentVariable(
                "SYSTEM_IMAGES_CLOUDFRONT"
              )}/images/EventImage${
                theme?.palette?.mode === "dark" ? "Dark" : ""
              }.png`
            }
            alt={organization?.name}
            sx={{
              maxHeight: "calc((100vw - 24px) * 0.5625)",
              "@media (min-width: 600px)": {
                maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)",
              },
              "@media (min-width: 900px)": {
                maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)",
              },
              "@media (min-width: 1218px)": {
                maxHeight: "215px",
              },
            }}
          />
          <CardHeader
            title={organization?.name}
            subheader={
              organization && distributorView
                ? `${getEnvironmentVariable("APP_NAME")} ID: ${organization.id}`
                : organization && isAdmin === true
                  ? moment(organization?.created_at).format(
                      "MMMM Do YYYY, HH:mm"
                    )
                  : organization?.distance !== undefined
                    ? `${
                        organization.distance > 10
                          ? parseInt(organization.distance)
                          : organization.distance.toFixed(2)
                      } ${organization.distanceType || "miles"}`
                    : null
            }
            action={
              isAdmin === true ? (
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    setOptionsAnchorEl(e.currentTarget);
                    //
                  }}
                >
                  <BCPEllipsisVertical />
                </IconButton>
              ) : null
            }
          />
        </CardActionArea>
        <CardContent>
          {organization.owner && (
            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex",
                }}
              >
                <Tooltip title="Organization Owner">
                  <BCPUser
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom",
                    }}
                  />
                </Tooltip>
              </Box>
              {userDisplayName(
                organization.owner.firstName,
                organization.owner.lastName
              )}
            </Typography>
          )}

          {organization.location && (
            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex",
                }}
              >
                <Tooltip title="Location">
                  <BCPLocationDot
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom",
                    }}
                  />
                </Tooltip>
              </Box>
              {cleanLocation(organization.location)}
            </Typography>
          )}

          {isAdmin === true && organization.approvalStatus && (
            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex",
                }}
              >
                <Tooltip title="Approval Status">
                  <BCPCircleInfo
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom",
                    }}
                  />
                </Tooltip>
              </Box>

              <Chip
                label={organization.approvalStatus}
                size="small"
                sx={{ textTransform: "capitalize" }}
                color={
                  organization.approvalStatus === "unapproved"
                    ? "warning"
                    : organization.approvalStatus === "approved"
                      ? "success"
                      : "error"
                }
              />
            </Typography>
          )}
        </CardContent>
      </Card>

      {isAdmin === true && (
        <>
          <OptionsMenu
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            organization={organization}
            deleteOrganization={deleteOrganization}
            updateOrganization={updateOrganization}
          />
        </>
      )}
    </>
  );
}

function OptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    organization,
    deleteOrganization,
    updateOrganization,
  } = props;
  const confirm = useConfirm();
  const open = Boolean(anchorEl);
  const [loading, setLoading] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button",
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: `Are you sure?`,
                description:
                  "Are you sure you want to approve this organization?",
                dialogProps: {
                  sx: {
                    zIndex: 99999,
                  },
                },
              });
            } catch (err) {
              return;
            }

            setLoading(true);
            try {
              var updatedOrg = await approveOrganization({
                organizationId: organization.id,
              });
              if (updateOrganization) {
                updateOrganization(updatedOrg);
              }
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
          }}
          disableRipple
        >
          Approve Organization
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            setApprovalStatus("rejected");
            setModalOpen(true);
          }}
          disableRipple
        >
          Reject Organization
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            setApprovalStatus("pending");
            setModalOpen(true);
          }}
          disableRipple
        >
          Pending Organization
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: `Are you sure?`,
                description:
                  "Are you sure you want to delete this organization?",
                dialogProps: {
                  sx: {
                    zIndex: 99999,
                  },
                },
              });
            } catch (err) {
              return;
            }
            try {
              await confirm({
                title: `Are you really sure?`,
                description:
                  "Are you really sure you want to delete this organization?",
                dialogProps: {
                  sx: {
                    zIndex: 99999,
                  },
                },
              });
            } catch (err) {
              return;
            }
            setLoading(true);
            try {
              await deleteData(`/gamestores/${organization.id}`);
              if (deleteOrganization) {
                deleteOrganization(organization.id);
              }
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
          }}
          disableRipple
        >
          Delete Organization
        </MenuItem>
      </StyledMenu>
      {(approvalStatus === "rejected" || approvalStatus === "pending") && (
        <ApprovalModal
          handleUpdates={(updates) => {
            updateOrganization({ ...organization, ...updates });
            setApprovalStatus(null);
          }}
          organization={organization}
          approvalStatus={approvalStatus}
          open={modalOpen}
          setOpen={setModalOpen}
        />
      )}
      {loading && <ModalLoadingFull />}
    </>
  );
}
